import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, Eye } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type FormSubmission = {
  id: string;
  form_id: string;
  entity_type: string | null;
  entity_id: string | null;
  status: string;
  submitted_by: string | null;
  submitted_by_name: string | null;
  submitted_by_email: string | null;
  created_at: string;
  updated_at: string;
};

type FormSubmissionValue = {
  id: string;
  field_id: string;
  valor: string;
  valor_json: any;
  form_fields: {
    nome: string;
    slug: string;
    tipo: string;
  };
};

interface FormSubmissionsViewerProps {
  formId: string;
  entityType?: string;
  entityId?: string;
}

export function FormSubmissionsViewer({
  formId,
  entityType,
  entityId,
}: FormSubmissionsViewerProps) {
  const [selectedSubmission, setSelectedSubmission] = useState<string | null>(null);

  const { data: submissions, isLoading } = useQuery({
    queryKey: ["form-submissions", formId, entityType, entityId],
    queryFn: async () => {
      let query = supabase
        .from("form_submissions")
        .select("*")
        .eq("form_id", formId)
        .order("created_at", { ascending: false });

      if (entityType) {
        query = query.eq("entity_type", entityType);
      }
      if (entityId) {
        query = query.eq("entity_id", entityId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as FormSubmission[];
    },
  });

  const { data: submissionDetails } = useQuery({
    queryKey: ["form-submission-details", selectedSubmission],
    queryFn: async () => {
      if (!selectedSubmission) return null;

      const { data: values, error } = await supabase
        .from("form_submission_values")
        .select(`
          *,
          form_fields (
            nome,
            slug,
            tipo
          )
        `)
        .eq("submission_id", selectedSubmission)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return values as FormSubmissionValue[];
    },
    enabled: !!selectedSubmission,
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      pendente: "outline",
      aprovado: "default",
      rejeitado: "destructive",
      rascunho: "secondary",
    };
    return (
      <Badge variant={variants[status] || "outline"}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatValue = (value: FormSubmissionValue) => {
    if (value.valor_json) {
      return Array.isArray(value.valor_json)
        ? value.valor_json.join(", ")
        : JSON.stringify(value.valor_json);
    }
    return value.valor;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Respostas do Formulário</CardTitle>
        </CardHeader>
        <CardContent>
          {!submissions || submissions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhuma resposta encontrada.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Enviado por</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissions.map((submission) => (
                  <TableRow key={submission.id}>
                    <TableCell>
                      {format(new Date(submission.created_at), "dd/MM/yyyy HH:mm", {
                        locale: ptBR,
                      })}
                    </TableCell>
                    <TableCell>
                      {submission.submitted_by_name ||
                        submission.submitted_by_email ||
                        "Anônimo"}
                    </TableCell>
                    <TableCell>{getStatusBadge(submission.status)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedSubmission(submission.id)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Ver Detalhes
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedSubmission} onOpenChange={() => setSelectedSubmission(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes da Resposta</DialogTitle>
          </DialogHeader>
          {submissionDetails && (
            <div className="space-y-4">
              {submissionDetails.map((value) => (
                <Card key={value.id}>
                  <CardContent className="pt-6">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-muted-foreground">
                        {value.form_fields.nome}
                      </p>
                      <p className="text-base">{formatValue(value)}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

